/** Short aliases are resolved before signup; old URLs and stored codes stay valid forever. */
const SHORT = /^~[A-Za-z0-9]{5,8}$/;
const received = new Map<string, string>();
const pending = new Map<string, Promise<string | undefined>>();
const owned = new Map<string, string>();
export const isShortReferralCode = (code: string) => SHORT.test(code);
export function cachedReferralUserId(code: string): string | undefined {
  return received.get(code);
}
export function cachedReferralCode(uid: string): string | undefined {
  if (owned.has(uid)) return owned.get(uid);
  try { const value = localStorage.getItem(`jungle.referralCode.v1:${uid}`); if (value && SHORT.test(value)) { owned.set(uid, value); received.set(value, uid); return value; } } catch { /* Optional cache. */ }
}
async function workerBase() {
  // Keep marketing's normal legacy decoder free of application-runtime imports.
  return (await import("../../constants/GlobalTypesAndEnumsAndConstants")).JUNGLE_BACKEND_DOMAIN_FOR_ROUTES_MIGRATED_TO_CLOUDFLARE_WORKER;
}
export async function resolveReferralCode(code: string): Promise<string | undefined> {
  if (!SHORT.test(code)) return undefined;
  if (received.has(code)) return received.get(code);
  if (pending.has(code)) return pending.get(code);
  const request = (async () => {
    try {
      const response = await fetch(`${await workerBase()}/users/resolve-referral-code?code=${encodeURIComponent(code)}`, { signal: AbortSignal.timeout(5000) });
      if (response.status >= 500 || response.status === 429) throw new Error("referral_resolution_unavailable");
      if (!response.ok) return undefined;
      const body = await response.json();
      if (typeof body?.user_id !== "string" || !/^[A-Za-z0-9_-]{1,128}$/.test(body.user_id)) return undefined;
      received.set(code, body.user_id);
      return body.user_id as string;
    } finally { pending.delete(code); }
  })();
  pending.set(code, request);
  return request;
}
/** Called once after sign-in; sync share handlers then reuse this alias, with legacy fallback offline. */
export async function ensureReferralCode(uid: string): Promise<string | undefined> {
  const cached = cachedReferralCode(uid); if (cached) return cached;
  const { getFirebaseAuthorizationHeaders } = await import("./firebase-auth-headers");
  const response = await fetch(`${await workerBase()}/users/referral-code`, {
    method: "POST", headers: { "Content-Type": "application/json", ...await getFirebaseAuthorizationHeaders() },
    body: JSON.stringify({ user_id: uid }), signal: AbortSignal.timeout(5000),
  });
  if (!response.ok) return undefined;
  const body = await response.json();
  if (!SHORT.test(body?.code)) return undefined;
  owned.set(uid, body.code); received.set(body.code, uid);
  try { localStorage.setItem(`jungle.referralCode.v1:${uid}`, body.code); } catch { /* Memory cache remains. */ }
  return body.code;
}
